'use client';

import { Box, Circle, Span, Stack, useSlotRecipe } from '@chakra-ui/react';
import type { MentionNodeAttrs, MentionOptions } from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionProps } from '@tiptap/suggestion';
import * as React from 'react';
import { LuUser } from 'react-icons/lu';
import VStackRow from '../VStackRow';

export interface TMentionUser {
  id: string;
  value: string;
  email?: string;
  avatar?: string;
}

export type MentionAttrs = MentionNodeAttrs;

export const filterMentionUsers = (users: TMentionUser[], query: string): TMentionUser[] => {
  if (!query) {
    return users;
  }

  const search = query.toLowerCase();

  return users.filter(
    (user) =>
      user.value.toLowerCase().includes(search) || (user.email ?? '').toLowerCase().includes(search)
  );
};

interface MentionListProps {
  items: TMentionUser[];
  selectedIndex: number;
  onSelect: (user: TMentionUser) => void;
}

// TODO: Make this UI more aligned to Voly
export const MentionList = React.forwardRef<HTMLDivElement, MentionListProps>(function MentionList(
  { items, selectedIndex, onSelect },
  ref
) {
  const menuRecipe = useSlotRecipe({ key: 'menu' });
  const styles = menuRecipe({ size: 'md' });
  const selectedRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      ref={ref}
      css={styles.content}
      role="listbox"
      aria-label="User mentions"
      maxH="15rem"
      overflowY="auto"
    >
      {items.map((item, index) => (
        <Box
          key={item.id}
          ref={index === selectedIndex ? selectedRef : undefined}
          css={styles.item}
          role="option"
          aria-selected={index === selectedIndex}
          data-highlighted={index === selectedIndex ? '' : undefined}
          onPointerDown={(event) => {
            event.preventDefault();
            onSelect(item);
          }}
        >
          <VStackRow gap="2.5" w="full">
            <Circle size="8" layerStyle="fill.muted">
              <LuUser size={16} />
            </Circle>

            <Stack align="start" gap="0" flex="1" minW="0">
              <Span textStyle="sm" fontWeight="medium">
                {item.value}
              </Span>

              {item.email && (
                <Span textStyle="xs" color="fg.muted">
                  {item.email}
                </Span>
              )}
            </Stack>
          </VStackRow>
        </Box>
      ))}
    </Box>
  );
});

export const createMentionSuggestion = (
  users: TMentionUser[],
  container?: HTMLElement | null
): MentionOptions<TMentionUser, MentionAttrs>['suggestion'] => ({
  char: '@',
  container: container ?? undefined,
  items: ({ query }) => filterMentionUsers(users, query),
  render: () => {
    const state: {
      component?: ReactRenderer<HTMLDivElement, MentionListProps>;
      unmount?: () => void;
      selectedIndex: number;
    } = { selectedIndex: 0 };

    const buildProps = (props: SuggestionProps<TMentionUser, MentionAttrs>): MentionListProps => ({
      items: props.items,
      selectedIndex: state.selectedIndex,
      onSelect: (user) => props.command({ id: user.id, label: user.value }),
    });

    const highlight = (index: number) => {
      state.selectedIndex = index;

      if (state.component) {
        state.component.updateProps({ ...state.component.props, selectedIndex: index });
      }
    };

    return {
      onStart: (props) => {
        state.selectedIndex = 0;
        state.component = new ReactRenderer(MentionList, {
          props: buildProps(props),
          editor: props.editor,
        });

        if (props.clientRect) {
          state.unmount = props.mount(state.component.element);
        }
      },
      onUpdate: (props) => {
        state.selectedIndex = Math.min(state.selectedIndex, Math.max(props.items.length - 1, 0));
        state.component?.updateProps(buildProps(props));
      },
      onKeyDown: (props) => {
        const items = state.component?.props.items ?? [];

        if (items.length === 0) {
          return false;
        }

        if (props.event.key === 'ArrowUp') {
          highlight((state.selectedIndex - 1 + items.length) % items.length);
          return true;
        }

        if (props.event.key === 'ArrowDown') {
          highlight((state.selectedIndex + 1) % items.length);
          return true;
        }

        if (props.event.key === 'Enter') {
          const item = items[state.selectedIndex];

          if (item) {
            state.component?.props.onSelect(item);
          }

          return true;
        }

        if (props.event.key === 'Escape') {
          state.unmount?.();
          return true;
        }

        return false;
      },
      onExit: () => {
        state.unmount?.();
        state.component?.destroy();
      },
    };
  },
});
